import { useSelector } from 'react-redux';
import { selectIsDebitor, selectIsFarmleiter, selectIsWarenempfaenger } from '../../store/Navigation.store';
import { useMemo } from 'react';
import { date, object, string } from 'yup';
import {
    createBestellpositionViewSchemaExistingBestellung,
    createDateSchema,
    createTimeSchema,
} from '../../shared/validation/bestellung/validation';
import { minDate } from '../../shared/helper/date-helper';
import { datumWEBisSchema, uhrzeitWEBisSchema } from '../../shared/validation/bestellung/schemas';
import { BestellpositionView } from '../../shared/types';
import { BestellungErpStatus } from '../../shared/types/enums';
import { isSackwareView } from '../../shared/helper/bestellungen-helper';
import { WUNSCHTERMIN_VALIDATION_ERRORS } from '../../shared/constants';
import { testDebitorWunschterminInFuture } from '../../shared/validation/bestellung/testFunctions';

/** Nur validieren, wenn mindestens eine nicht-stornierte Sackware-Position noch nicht ausgeliefert ist */
const mussWunschterminSackwareValidiertWerden = (bestellpositionenView: BestellpositionView[]): boolean => {
    const sackwaren = bestellpositionenView.filter(isSackwareView);
    const nichtStornierteSackwaren = sackwaren.filter((s) => !s.storno);
    if (nichtStornierteSackwaren.length === 0) return false;
    return nichtStornierteSackwaren.some(
        (s) =>
            (s as BestellpositionView & { erpStatus?: string; liefer_status?: string }).erpStatus !== BestellungErpStatus.G &&
            (s as BestellpositionView & { erpStatus?: string; liefer_status?: string }).liefer_status !== 'KL'
    );
};

// NOTE: Ignore because we could not get yup typings working correctly
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useBestellansichtFormSchema = (storniereBestellung: boolean, useLooseValidation = false) => {
    const isDebitor = useSelector(selectIsDebitor);
    const isFarmleiter = useSelector(selectIsFarmleiter);
    const isWarenempfaenger = useSelector(selectIsWarenempfaenger);
    return useMemo(() => {
        if (!storniereBestellung) {
            const DebitorSchema = object({
                bestellpositionenView: createBestellpositionViewSchemaExistingBestellung(isDebitor, isFarmleiter),
                datumDebitor: date().when('bestellpositionenView', {
                    is: mussWunschterminSackwareValidiertWerden,
                    then: () => createDateSchema(minDate).required(WUNSCHTERMIN_VALIDATION_ERRORS.MISSING_DATE),
                    otherwise: (schema) => schema.notRequired(),
                }),
                uhrzeitDebitor: string().when('bestellpositionenView', {
                    is: mussWunschterminSackwareValidiertWerden,
                    then: () =>
                        createTimeSchema()
                            .required(WUNSCHTERMIN_VALIDATION_ERRORS.MISSING_TIME)
                            .test(
                                'debitor-wunschtermin-in-future',
                                WUNSCHTERMIN_VALIDATION_ERRORS.TIME_IN_THE_PAST,
                                testDebitorWunschterminInFuture
                            ),
                    otherwise: (schema) => schema.notRequired(),
                }),
                datumWEVon: createDateSchema(minDate, false),
                datumWEBis: datumWEBisSchema,
                uhrzeitWEVon: createTimeSchema(false),
                uhrzeitWEBis: uhrzeitWEBisSchema,
            });

            const WarenempfaengerSchema = object({
                bestellpositionenView: createBestellpositionViewSchemaExistingBestellung(isDebitor, isFarmleiter),
                datumWEVon: createDateSchema(minDate),
                datumWEBis: datumWEBisSchema,
                uhrzeitWEVon: createTimeSchema(false),
                uhrzeitWEBis: uhrzeitWEBisSchema,
            });

            return isWarenempfaenger ? WarenempfaengerSchema : DebitorSchema;
        }
        return null;
    }, [isDebitor, isFarmleiter, isWarenempfaenger, storniereBestellung, useLooseValidation]);
};

export const useBestellansichtFormSchemaLoose = (storniereBestellung: boolean): ReturnType<typeof useBestellansichtFormSchema> => {
    return useBestellansichtFormSchema(storniereBestellung, true);
};
